import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sigma, FileDown, Sparkles, Zap, Star, ArrowRight, Calculator } from "lucide-react";

/**
 * Formula Vault — showcase all class-wise formula sheets on one striking page.
 * Design: glowing gradient hero, oversized Sigma glyph, per-class glass cards,
 * on-hover shimmer. Aligns with the sunset (pink/peach/yellow/sky) palette.
 */

const CLASS_ACCENTS = {
  "6":  { from: "from-pink-300",   to: "to-orange-300", ring: "ring-pink-300/30",   glow: "shadow-pink-300/20" },
  "7":  { from: "from-orange-300", to: "to-yellow-300", ring: "ring-orange-300/30", glow: "shadow-orange-300/20" },
  "8":  { from: "from-yellow-300", to: "to-lime-300",   ring: "ring-yellow-300/30", glow: "shadow-yellow-300/20" },
  "9":  { from: "from-sky-300",    to: "to-cyan-300",   ring: "ring-sky-300/30",    glow: "shadow-sky-300/20" },
  "10": { from: "from-fuchsia-300", to: "to-pink-300",  ring: "ring-fuchsia-300/30", glow: "shadow-fuchsia-300/20" },
};

function SheetCard({ sheet }) {
  const accent = CLASS_ACCENTS[sheet.class_id] || CLASS_ACCENTS["10"];
  return (
    <a
      href={sheet.file_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`formula-card-${sheet.class_id}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0f1a34]/80 p-6 ring-1 ${accent.ring} hover:border-white/30 hover:shadow-2xl ${accent.glow} transition-all duration-300`}
    >
      {/* animated shimmer */}
      <div className={`absolute -inset-1 opacity-0 group-hover:opacity-100 bg-gradient-to-r ${accent.from} ${accent.to} blur-2xl transition-opacity duration-500 -z-0`} />
      <div className={`absolute top-0 right-0 w-28 h-28 -mr-8 -mt-8 rounded-full bg-gradient-to-br ${accent.from} ${accent.to} opacity-20 blur-2xl group-hover:opacity-40 transition-opacity`} />
      <div className="relative z-10">
        <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold bg-gradient-to-r ${accent.from} ${accent.to} bg-clip-text text-transparent mb-4`}>
          <Sparkles className="w-3 h-3 text-orange-300" />
          Class {sheet.class_id}
        </div>
        <div className="flex items-baseline gap-3 mb-2">
          <Sigma className={`w-8 h-8 bg-gradient-to-br ${accent.from} ${accent.to} bg-clip-text text-transparent`} strokeWidth={2.5} />
          <div className="font-display text-2xl font-bold text-zinc-100">
            Class {sheet.class_id}
          </div>
        </div>
        <div className="text-sm text-zinc-400 mb-6">
          Every formula you need — algebra, geometry, mensuration, trigonometry — in one printable sheet.
        </div>
        <div className={`inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 bg-gradient-to-r ${accent.from} ${accent.to} text-slate-900 shadow-lg group-hover:scale-105 transition-transform`}>
          <FileDown className="w-4 h-4" />
          Download sheet
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </a>
  );
}

export default function Formulas() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/formulas")
      .then((r) => setSheets(r.data.formula_sheets || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fade-up">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a1a3a] via-[#1e1547] to-[#3a1a3a] p-8 md:p-12 mb-10">
        {/* orbs */}
        <div className="absolute top-0 -right-20 w-80 h-80 rounded-full bg-pink-300/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-yellow-300/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.04]">
          <Sigma className="w-[440px] h-[440px]" strokeWidth={1.5} />
        </div>

        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full bg-pink-300/10 text-pink-300 border border-pink-300/30 mb-4">
            <Zap className="w-3 h-3" />
            New · Free download
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3">
            The <span className="bg-gradient-to-r from-pink-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent">Formula Vault</span>
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl">
            Every important CBSE Maths formula — algebra, geometry, mensuration, trigonometry, probability — packed into one clean sheet per class. Print it, pin it, own it.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-zinc-400">
            <div className="flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5 text-yellow-300 fill-yellow-300" />
              <span>Handpicked by CBSE-focused teachers</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calculator className="w-3.5 h-3.5 text-orange-300" />
              <span>Classes 6 – 10</span>
            </div>
            <div className="flex items-center gap-1.5">
              <FileDown className="w-3.5 h-3.5 text-emerald-300" />
              <span>One-tap download</span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400 py-10 text-center">Loading formula sheets…</div>
      ) : sheets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 bg-[#0f1a34] p-8 text-center text-zinc-500 text-sm">
          Formula sheets are being prepared. Check back shortly.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="formula-grid">
          {sheets.map((s) => (
            <SheetCard key={s.class_id} sheet={s} />
          ))}
        </div>
      )}

      <div className="mt-10 rounded-2xl border border-white/5 bg-white/[0.02] p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-lg font-semibold text-zinc-100 mb-1">
            Head back and start practising
          </div>
          <div className="text-sm text-zinc-500">
            Pick your class and turn these formulas into board-ready muscle memory.
          </div>
        </div>
        <Link
          to="/classes"
          className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-900 transition-colors"
          data-testid="back-to-classes"
        >
          Explore chapters <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
